import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva } from "class-variance-authority"
import { X } from "lucide-react"
import { cn } from "../../lib/utils"

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed top-4 right-4 z-[100] flex max-h-screen w-full flex-col-reverse gap-3 p-0 sm:bottom-auto sm:right-4 sm:top-4 sm:flex-col md:max-w-[440px]",
      className
    )}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-start justify-between space-x-3 overflow-hidden rounded-xl border-0 p-4 pr-10 shadow-2xl transition-all duration-500 backdrop-blur-md data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full transform-gpu hover:scale-[1.02] hover:shadow-3xl",
  {
    variants: {
      variant: {
        default: "bg-gradient-to-br from-white via-gray-50 to-white text-gray-900 border border-gray-200/60 shadow-xl ring-1 ring-gray-900/5",
        destructive: "bg-gradient-to-br from-red-500 via-red-600 to-red-700 text-white shadow-2xl shadow-red-500/30 ring-1 ring-red-400/20",
        success: "bg-gradient-to-br from-emerald-500 via-green-600 to-emerald-700 text-white shadow-2xl shadow-emerald-500/30 ring-1 ring-emerald-400/20",
        warning: "bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 text-white shadow-2xl shadow-amber-500/30 ring-1 ring-amber-400/20",
        info: "bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 text-white shadow-2xl shadow-blue-500/30 ring-1 ring-blue-400/20",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Toast = React.forwardRef(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  )
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-lg border border-white/30 bg-white/15 px-3 text-sm font-medium transition-all duration-300 hover:bg-white/25 hover:scale-105 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 backdrop-blur-md active:scale-95",
      className
    )}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-2 top-2 rounded-full p-1.5 text-foreground/60 opacity-0 transition-all duration-300 hover:text-foreground hover:bg-black/10 hover:scale-110 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 group-hover:opacity-100 group-[.destructive]:text-white/70 group-[.destructive]:hover:text-white group-[.destructive]:hover:bg-white/15 group-[.destructive]:hover:shadow-lg group-[.success]:text-white/70 group-[.success]:hover:text-white group-[.success]:hover:bg-white/15 group-[.success]:hover:shadow-lg group-[.warning]:text-white/70 group-[.warning]:hover:text-white group-[.warning]:hover:bg-white/15 group-[.warning]:hover:shadow-lg group-[.info]:text-white/70 group-[.info]:hover:text-white group-[.info]:hover:bg-white/15 group-[.info]:hover:shadow-lg",
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-sm font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-sm opacity-90 leading-relaxed", className)}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

export {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
}